import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from 'react'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { FEATURE_MINIMUM_TIER, PRICING_TIERS, type GatedFeature } from '../../config/PricingConfig'
import { getNlsString } from '../../nls/nlsRuntime'
import { Pressable } from './Pressable'
import './chip.css'

export type ChipSize = 'md' | 'sm'

export type StatusPillTone =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'muted'

const STATUS_TONE_CLASS: Record<StatusPillTone, string> = {
  neutral: 'statusPill--neutral',
  info: 'statusPill--info',
  success: 'statusPill--success',
  warning: 'statusPill--warning',
  danger: 'statusPill--danger',
  muted: 'statusPill--muted',
}

export type StatusChipProps = {
  tone?: StatusPillTone
  icon?: ReactNode
  children: ReactNode
  size?: ChipSize
  className?: string
} & React.HTMLAttributes<HTMLSpanElement>

/**
 * §11.3 shared primitive — one component for the workflow-status pill,
 * sync-state pill, review-signal readiness pill, and other single-token
 * status badges. Consumers pass a `tone` that maps to a tint band; the
 * default palette is the same slate/indigo scale used everywhere else in
 * the member shell.
 */
export const StatusChip = ({
  tone = 'neutral',
  icon,
  children,
  size = 'md',
  className,
  ...rest
}: StatusChipProps) => (
  <span
    {...rest}
    className={[
      'dsChip',
      'dsChip--status',
      `dsChip--${size}`,
      'statusPill',
      STATUS_TONE_CLASS[tone],
      size === 'sm' ? 'statusPill--compact' : '',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {icon ? <span className="statusPillIcon" aria-hidden="true">{icon}</span> : null}
    <span className="statusPillLabel">{children}</span>
  </span>
)

export type FilterChipProps = {
  selected?: boolean
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
  disabled?: boolean
  count?: number
  children: ReactNode
  selectionRole?: 'toggle' | 'tab' | 'radio' | 'checkbox'
  size?: ChipSize
  className?: string
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'>

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  (
    {
      selected = false,
      onClick,
      disabled = false,
      count,
      children,
      selectionRole = 'toggle',
      size = 'md',
      className,
      ...rest
    },
    ref,
  ) => {
    const isTab = selectionRole === 'tab'
    const isCheckable = selectionRole === 'radio' || selectionRole === 'checkbox'
    return (
      <Pressable
        ref={ref}
        role={isTab ? 'tab' : isCheckable ? selectionRole : undefined}
        aria-pressed={!isTab && !isCheckable ? selected : undefined}
        aria-selected={isTab ? selected : undefined}
        aria-checked={isCheckable ? selected : undefined}
        disabled={disabled}
        onClick={onClick}
        className={[
          'dsChip',
          'dsChip--filter',
          `dsChip--${size}`,
          !isTab && !isCheckable ? 'adaptiveMultiFilterChip' : '',
          selected ? 'isActive' : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      >
        <span>{children}</span>
        {typeof count === 'number' ? <strong>{count}</strong> : null}
      </Pressable>
    )
  },
)
FilterChip.displayName = 'FilterChip'

export type TagChipProps = {
  children: ReactNode
  onRemove?: () => void
  removeLabel: string
  size?: ChipSize
  className?: string
  disabled?: boolean
}

export const TagChip = ({
  children,
  onRemove,
  removeLabel,
  size = 'md',
  className,
  disabled,
}: TagChipProps) => (
  <span
    className={[
      'dsChip',
      'dsChip--tag',
      `dsChip--${size}`,
      'dsTag',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {children}
    {onRemove ? (
      <Pressable
        aria-label={removeLabel}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
      >
        {'\u00D7'}
      </Pressable>
    ) : null}
  </span>
)

export type CountBadgeTone = 'default' | 'danger' | 'solid'

export type CountBadgeProps = {
  value: number
  max?: number
  tone?: CountBadgeTone
  size?: ChipSize
  className?: string
}

export const CountBadge = ({
  value,
  max = 9,
  tone = 'default',
  size = 'md',
  className,
}: CountBadgeProps) => {
  const displayValue = value > max ? `${max}+` : String(value)
  return (
    <span
      className={[
        'dsChip',
        'dsChip--count',
        `dsChip--${size}`,
        'countBadge',
        tone === 'danger' ? 'countBadge--danger' : '',
        tone === 'solid' ? 'countBadge--solid' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {displayValue}
    </span>
  )
}

export type LockBadgeProps = {
  feature?: GatedFeature
  label?: string
  size?: ChipSize
  className?: string
}

export const LockBadge = ({
  feature,
  label,
  size = 'md',
  className,
}: LockBadgeProps) => {
  const minimum = feature
    ? (PRICING_TIERS.find((tier) => tier.id === FEATURE_MINIMUM_TIER[feature])?.name ??
       getNlsString('NLSCommonLockBadgePaidTier'))
    : getNlsString('NLSCommonLockBadgePaidTier')
  const defaultLabel = getNlsString('NLSCommonLockBadgeLabel')
  const effectiveLabel = label ?? defaultLabel
  const title = getNlsString('NLSCommonLockBadgeRequires', { tier: minimum })

  const openPricing = () => {
    window.history.pushState(null, '', '/purchase')
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  return (
    <Pressable
      className={[
        'dsChip',
        'dsChip--lock',
        `dsChip--${size}`,
        'upgradeLockBadge',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={openPricing}
      title={title}
    >
      <LockRoundedIcon fontSize="inherit" />
      <span>{effectiveLabel}</span>
      <small>{minimum}</small>
    </Pressable>
  )
}

export type ProBadgeProps = {
  label?: string
  size?: ChipSize
  className?: string
}

export const ProBadge = ({
  label,
  size = 'md',
  className,
}: ProBadgeProps) => (
  <span
    className={[
      'dsChip',
      'dsChip--pro',
      `dsChip--${size}`,
      'proBadge',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {label ?? getNlsString('NLSCommonProBadge')}
  </span>
)
